// @ts-nocheck
import * as React from 'react';
import Icon from '../../components/common/Icon';
import { PageHero, Section, Sparkline, SendBtn, PresentBtn } from '../shared/InternalShared';

// feedback.jsx — AI Feedback & Surveys. Accent: green.
// "Parents speak. The school listens. Visibly."

export default function PageFeedback({ student, accent, sendToParent }) {
  const heroRight = (
    <div className="ip-card ip-rise" style={{ padding: 22, background: "var(--bg)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div className="ip-stat__n" style={{ color: "var(--accent)" }}>4.6<sup style={{ fontSize: "0.4em" }}>/5</sup></div>
          <div className="ip-stat__l">Parent satisfaction</div>
        </div>
        <Sparkline points={[4.0, 4.1, 4.0, 4.3, 4.4, 4.5, 4.6]} width={130} height={44} />
      </div>
      <hr className="ip-hr" style={{ margin: "16px 0" }} />
      <div style={{ display: "grid", gap: 9 }}>
        {[
          { l: "Positive sentiment", v: 78, c: "var(--green)" },
          { l: "Neutral", v: 16, c: "var(--fg-tertiary)" },
          { l: "Needs attention", v: 6, c: "var(--amber)" },
        ].map((s) => (
          <div key={s.l}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 4 }}>
              <span style={{ color: "var(--fg-secondary)", fontWeight: 600 }}>{s.l}</span>
              <span style={{ color: "var(--fg)", fontWeight: 700 }}>{s.v}%</span>
            </div>
            <div className="ip-bar"><i style={{ width: s.v + "%", background: s.c }} /></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="ip" style={{ "--accent": accent }}>
      <PageHero
        eyebrow="AI Feedback & Surveys"
        icon="comms"
        title={<>Parents speak.<br/>The school <span className="ip-accent">listens. Visibly.</span></>}
        lead="Every voice captured, every theme understood, every change shown back to the parent who asked for it — the two-way trust loop top institutions are built on."
        actions={<>
          <SendBtn onClick={() => sendToParent("30-second parent survey")} label="Send a survey" />
          <PresentBtn onClick={() => sendToParent("Feedback loop walkthrough")} />
        </>}
        trust={{ label: "Inspired by", items: [
          { label: "Quality Council of India" },
          { label: "NEP 2020" },
        ] }}
        right={heroRight}
      />

      {/* The loop */}
      <Section eyebrow="The trust loop" title="Feedback that closes, not collects" hint="Most schools collect feedback. The loop only builds trust when the parent sees it act.">
        <div className="ip-loop">
          {[
            { n: "01", t: "Collect", d: "AI-built surveys reach parents on app, WhatsApp or a call — in their language.", ic: "comms" },
            { n: "02", t: "Analyse", d: "Sentiment, themes and concern patterns surfaced automatically.", ic: "analytics" },
            { n: "03", t: "Act", d: "Data-driven recommendations routed to the right team, with attrition risk flagged.", ic: "growth" },
            { n: "04", t: "Notify", d: "Parents are told when their feedback was implemented. The loop closes.", ic: "check" },
          ].map((s) => (
            <div key={s.n} className="ip-loop__step">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div className="ip-iconbox" style={{ width: 30, height: 30, borderRadius: 8 }}><Icon name={s.ic} size={15} /></div>
                <span className="ip-loop__n">{s.n}</span>
              </div>
              <div className="ip-loop__t">{s.t}</div>
              <div className="ip-loop__d">{s.d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* You said / we did + survey preview */}
      <Section eyebrow="Proof the loop closed" title="“You said → We did”" style={{ paddingTop: 4 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 16, alignItems: "start" }}>
          <div style={{ display: "grid", gap: 12 }}>
            {[
              { said: "Bus arrives late on rainy days.", did: "Two monsoon routes added — 0 late arrivals since." },
              { said: "We don't hear about progress often enough.", did: "Weekly 360° updates now sent in your language." },
              { said: "Canteen menu lacks variety.", did: "Nutritionist-reviewed menu, refreshed monthly." },
            ].map((r, i) => (
              <div key={i} className="ip-said">
                <div className="ip-said__col-l">
                  <div className="ip-said__k ip-muted">You said</div>
                  {r.said}
                </div>
                <div className="ip-said__arrow"><Icon name="arrow-right" size={20} stroke={2} /></div>
                <div className="ip-said__col-r">
                  <div className="ip-said__k" style={{ color: "var(--accent)" }}>We did</div>
                  {r.did}
                </div>
              </div>
            ))}
          </div>

          {/* AI-generated survey preview */}
          <div className="ip-card" style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div className="ip-iconbox" style={{ width: 30, height: 30, borderRadius: 8 }}><Icon name="sparkles" size={15} /></div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--fg)" }}>AI-generated · 30 seconds</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--fg)", marginTop: 14, lineHeight: 1.4 }}>How supported does {student.child.split(" ")[0]} feel this term?</div>
            <div style={{ display: "flex", gap: 7, marginTop: 12 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <span key={n} style={{ flex: 1, textAlign: "center", padding: "10px 0", borderRadius: 9, border: "1px solid var(--border-strong)", fontSize: 14, fontWeight: 700, color: n === 4 ? "#fff" : "var(--fg-secondary)", background: n === 4 ? "var(--accent)" : "var(--bg)" }}>{n}</span>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--fg-tertiary)", marginTop: 10, lineHeight: 1.5 }}>Approval workflow · quarterly cadence · fully customisable. Reaches every parent on app, WhatsApp & call.</div>
          </div>
        </div>
      </Section>

      {/* Touchpoints */}
      <Section eyebrow="What parents weigh in on" title="Every touchpoint, one signal" style={{ paddingTop: 4 }}>
        <div className="ip-grid ip-grid--2">
          <div className="ip-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--fg-tertiary)", textTransform: "uppercase", letterSpacing: "0.12em" }}>School experience</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 12 }}>
              {["Hygiene", "Infrastructure", "Timings", "Extracurriculars", "Transport", "Canteen"].map((t) => (
                <span key={t} className="ip-tag ip-tag--mute" style={{ fontSize: 11.5, padding: "5px 11px" }}>{t}</span>
              ))}
            </div>
          </div>
          <div className="ip-card" style={{ padding: 18 }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: "var(--fg-tertiary)", textTransform: "uppercase", letterSpacing: "0.12em" }}>AI experiences</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 12 }}>
              {["AI teachers", "Childcare counselling", "Parental helpdesk", "360° reports", "Multilingual comms"].map((t) => (
                <span key={t} className="ip-tag" style={{ fontSize: 11.5, padding: "5px 11px" }}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <div className="ip-band" style={{ "--accent": accent }}>
        <span className="ip-band__eyebrow">Why it converts</span>
        <div className="ip-band__title">Listening, made visible, becomes loyalty.</div>
        <div className="ip-band__grid" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
          {[
            { n: "↑ Retention", l: "Parents who feel heard, stay" },
            { n: "↑ Satisfaction", l: "Higher term-on-term scores" },
            { n: "↑ Referrals", l: "Trust travels by word of mouth" },
            { n: "↓ Attrition risk", l: "Flagged before it's a decision" },
          ].map((s) => (
            <div key={s.l}>
              <div className="ip-band__stat-n">{s.n}</div>
              <div className="ip-band__stat-l">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}




